import React, { PureComponent } from 'react';
import { TimePeriod } from '../../../../model/CORE';
import { RadRestriction, RadRestrictions } from '../../../../model/SOL49';
import TimeUtils from '../../../../common/util/TimeUtils';
import AppContext from '../../../../common/context/AppContext';
import { Flex } from '../../../common/Flex';
import { LabelDisplayMode, TimePeriodSelector } from '../../../map/control/TimePeriodSelector';
import memoizeOne from 'memoize-one';
import { SINGLE_SELECTION, SingleSelection } from '../../../../common/table/ManagedTable';
import RadRestrictionTable, { QualifiedRadRestriction, RadRestrictionsColumn } from '../table/RadRestrictionTable';
import { Form, Select, Space } from 'antd';
import FormItem from 'antd/lib/form/FormItem';

interface RadListPanelProps {
    selectedTimePeriod: TimePeriod;
    applicableRadRestrictions: RadRestrictions;
    selectedRadRestriction: RadRestriction;
    onSelectRadRestriction: (restriction: RadRestriction) => void;
    onSelectedTimePeriodChange: (tp: TimePeriod) => void;
}

interface RadListPanelState {
    selectedTimePeriod?: TimePeriod;
    radFilterType: string;
}

export class RadListPanel extends PureComponent<RadListPanelProps, RadListPanelState> {
    constructor(props: RadListPanelProps) {
        super(props);
        this.state = {
            selectedTimePeriod: this.props.selectedTimePeriod,
            radFilterType: 'PRE_AGREED',
        };
    }

    /**
     * HANDLER
     */

    private onSelectedTimePeriodChange = (selectedTimePeriod: TimePeriod) => {
        this.setState({
            selectedTimePeriod: selectedTimePeriod,
        });
    };

    private radRestrictionSelection = memoizeOne(
        (selection: QualifiedRadRestriction): SingleSelection<QualifiedRadRestriction> => ({
            selectionMode: SINGLE_SELECTION,
            onSelectionChange: qualifiedRestriction =>
                this.props.onSelectRadRestriction(qualifiedRestriction.restriction),
            selectedItem: selection,
            selectionMatcher: (element: QualifiedRadRestriction, currentSelection: QualifiedRadRestriction) => {
                return (
                    currentSelection?.restriction != null &&
                    element?.restriction != null &&
                    element.restriction.id === currentSelection.restriction.id
                );
            },
        }),
    );

    /**
     * RENDER
     */

    render() {
        let data: QualifiedRadRestriction[] = [];
        if (this.props.applicableRadRestrictions) {
            data = [
                ...this.props.applicableRadRestrictions.offLoadingRadRestrictions.map(restriction => {
                    return { restriction: restriction, type: 'OFFLOAD' } as QualifiedRadRestriction;
                }),
                ...this.props.applicableRadRestrictions.onLoadingRadRestrictions.map(restriction => {
                    return { restriction: restriction, type: 'ONLOAD' } as QualifiedRadRestriction;
                }),
                ...this.props.applicableRadRestrictions.offLoadingAndOnloadingRadRestrictions.map(restriction => {
                    return { restriction: restriction, type: 'ONLOAD_OFFLOAD' } as QualifiedRadRestriction;
                }),
            ];
        }

        if (this.state.radFilterType && this.state.radFilterType === 'DRAFT') {
            data = data.filter(rad => rad.restriction.preAgreed === false);
        } else if (this.state.radFilterType && this.state.radFilterType === 'PRE_AGREED') {
            data = data.filter(rad => rad.restriction.preAgreed === true);
        }

        const columns = [
            RadRestrictionsColumn.RAD_RESTRICTION_SELECTED_COLUMN,
            RadRestrictionsColumn.RAD_RESTRICTION_ID,
            RadRestrictionsColumn.RAD_RESTRICTION_STATUS,
            RadRestrictionsColumn.RAD_RESTRICTION_TYPE,
        ];
        return (
            <Flex grow={true} className="applicable-rad-restrictions-panel" direction="vertical">
                <Flex direction={'vertical'} grow={false} style={{ gap: '1em' }}>
                    <Flex grow={false} direction="vertical" style={{ gap: '1em', padding: '.4em' }}>
                        {this.renderHeaderTimeSlider(this.getFullDayTimePeriod())}
                    </Flex>
                    <Flex direction={'vertical'}>{this.renderHeaderFilters()}</Flex>
                </Flex>
                <Flex grow={true} style={{ minHeight: '30em' }} direction="vertical">
                    <RadRestrictionTable
                        data={data}
                        noDataMessage={'No Rad Restriction found'}
                        selection={this.radRestrictionSelection({ restriction: this.props.selectedRadRestriction })}
                        columnResizeDisabled={true}
                        columns={columns}
                    />
                </Flex>
            </Flex>
        );
    }

    renderHeaderTimeSlider(fullDayTimePeriod: TimePeriod) {
        const currentTimeWithoutSeconds = TimeUtils.withoutSeconds(AppContext.getCurrentTime());
        return (
            <>
                <Flex grow={false}>
                    <span className="detailed-subpanel-title">TIME PERIOD</span>
                </Flex>
                <Flex direction={'vertical'} className={'tv-loadbar'} grow={false}>
                    <Flex style={{ alignItems: 'center' }}>
                        <TimePeriodSelector
                            width="23em"
                            value={this.state.selectedTimePeriod}
                            labelDisplayMode={LabelDisplayMode.ABSOLUTE}
                            currentTime={currentTimeWithoutSeconds}
                            markIntervalMs={60 * TimeUtils.MIN_TO_MS}
                            currentTimeLineColor="#E6E6E6"
                            timePeriodRange={fullDayTimePeriod}
                            hideDownLabels={true}
                            hasManualHandler
                            onChange={this.onSelectedTimePeriodChange}
                            onSelectedPeriodConfirmed={(tp: TimePeriod) => {
                                this.onSelectedTimePeriodChange(tp);
                                this.props.onSelectedTimePeriodChange(tp);
                            }}
                        />
                    </Flex>
                </Flex>
            </>
        );
    }

    renderHeaderFilters() {
        return (
            <>
                <Flex grow={false}>
                    <span className="detailed-subpanel-title">FILTERS</span>
                </Flex>
                <Flex grow={false}>
                    <Form size={'small'}>
                        <Space.Compact>
                            <FormItem noStyle>
                                <Select
                                    value={this.state.radFilterType ? this.state.radFilterType : 'PRE_AGREED'}
                                    style={{ width: '8em' }}
                                    onChange={val => this.setState({ radFilterType: val })}>
                                    <Select.Option value={'ALL'}>All</Select.Option>
                                    <Select.Option value={'PRE_AGREED'}>Pre Agreed</Select.Option>
                                    <Select.Option value={'DRAFT'}>Draft</Select.Option>
                                </Select>
                            </FormItem>
                        </Space.Compact>
                    </Form>
                </Flex>
            </>
        );
    }

    private getFullDayTimePeriod() {
        const timeFrom = TimeUtils.withoutMinutes(TimeUtils.withoutSeconds(AppContext.getCurrentTime()));
        timeFrom.setUTCHours(0);
        const timeTo = TimeUtils.withoutMinutes(TimeUtils.withoutSeconds(AppContext.getCurrentTime()));
        timeTo.setUTCHours(24);
        return { from: timeFrom, to: timeTo };
    }
}
